import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalPath?: string;
  ogTitle?: string;
  ogDescription?: string;
  /** Optional JSON-LD object injected as a per-page <script type="application/ld+json"> */
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
  /** Overrides the sitewide robots directive for this route. */
  robots?: string;
}

const PAGE_LD_ID = "page-jsonld";

const setMeta = (selector: string, attr: string, value: string) => {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

const SEOHead = ({
  title,
  description,
  canonicalPath,
  ogTitle,
  ogDescription,
  structuredData,
  robots,
}: SEOHeadProps) => {
  useEffect(() => {
    document.title = title;

    setMeta('meta[name="description"]', "content", description);

    // Robots — restore the sitewide default on unmount so it never leaks to another route.
    const robotsEl = document.querySelector('meta[name="robots"]');
    const previousRobots = robotsEl?.getAttribute("content") ?? null;
    if (robots && robotsEl) robotsEl.setAttribute("content", robots);

    // Open Graph
    setMeta('meta[property="og:title"]', "content", ogTitle || title);
    setMeta('meta[property="og:description"]', "content", ogDescription || description);
    if (canonicalPath) {
      setMeta('meta[property="og:url"]', "content", `https://www.skandortech.ai${canonicalPath}`);
    }

    // Twitter
    setMeta('meta[name="twitter:title"]', "content", ogTitle || title);
    setMeta('meta[name="twitter:description"]', "content", ogDescription || description);

    // Canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonicalPath) {
      if (!canonicalLink) {
        canonicalLink = document.createElement("link");
        canonicalLink.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute("href", `https://www.skandortech.ai${canonicalPath}`);
    }

    // Per-page JSON-LD
    const existing = document.getElementById(PAGE_LD_ID);
    if (existing) existing.remove();
    if (structuredData) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = PAGE_LD_ID;
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    return () => {
      const cleanup = document.getElementById(PAGE_LD_ID);
      if (cleanup) cleanup.remove();
      if (robots && robotsEl && previousRobots !== null) {
        robotsEl.setAttribute("content", previousRobots);
      }
    };
  }, [title, description, canonicalPath, ogTitle, ogDescription, structuredData, robots]);

  return null;
};

export default SEOHead;
